import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import "./index.css";

interface SaveVersionModalProps {
  showVersionModal: boolean;
  setShowVersionModal: (show: boolean) => void;
  newVersionName: string;
  setNewVersionName: (name: string) => void;
  saveNewVersion: (makeActive?: boolean) => void;
  isSaving: boolean;
  allPlans: any[];
  setVersionError: (error: string | null) => void;
  versionError: string | null;
}

interface FormData {
  versionName: string;
}

const SaveVersionModal: React.FC<SaveVersionModalProps> = ({
  showVersionModal,
  setShowVersionModal,
  newVersionName,
  setNewVersionName,
  saveNewVersion,
  isSaving,
  allPlans,
  setVersionError,
  versionError,
}) => {
  const { register, handleSubmit, formState: { errors }, setError, setValue, trigger } = useForm<FormData>({
    defaultValues: { versionName: newVersionName },
    mode: "onChange",
  });

  const existingPlanNames = allPlans.map((plan: any) => plan.allocationPlanVersion);

  useEffect(() => {
    setValue("versionName", newVersionName);
    trigger("versionName"); 
  }, [newVersionName, setValue, trigger]);

  const onSubmit = async (data: FormData) => {
    if (existingPlanNames.includes(data.versionName.trim())) {
      setError("versionName", {
        type: "manual",
        message: "This allocation plan name already exists. Please use a different name.",
      });
      return;
    }

    setNewVersionName(data.versionName);
    setVersionError(null);
    await saveNewVersion(false);
  };

  if (!showVersionModal) return null;

  return (
    <div className="save-version-modal-overlay">
      <div className="save-version-modal">
        <h3 className="save-version-modal__title">Save New Version</h3>
        <div className="save-version-modal__content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="save-version-modal__input-container">
              <label className="save-version-modal__label">Version Name</label>
              <input
                type="text"
                {...register("versionName", {
                  required: "Please enter a version name",
                  validate: (value) =>
                    !existingPlanNames.includes(value.trim()) ||
                    "This allocation plan name already exists. Please use a different name.",
                })}
                onChange={(e) => {
                  setNewVersionName(e.target.value);
                  setVersionError(null); 
                  trigger("versionName"); 
                }}
                className="save-version-modal__input"
                placeholder="Enter version name..."
                autoFocus
              />
              {(errors.versionName || versionError) && (
                <span className="text-danger">
                  {errors.versionName?.message || versionError}
                </span>
              )}
            </div>
            <div className="save-version-modal__description">
              Create a new version of your allocation plan.
            </div>
          </form>
        </div>
        <div className="save-version-modal__buttons">
          <button
            onClick={() => {
              setShowVersionModal(false);
              setVersionError(null);
              setNewVersionName("");
            }}
            disabled={isSaving}
            className="save-version-modal__button save-version-modal__button--secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !!errors.versionName}
            className="save-version-modal__button save-version-modal__button--primary"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveVersionModal;